import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';
import { FASH_LOGO } from '../../constants/brand';

type UserRole = 'shopper' | 'business' | 'driver';

const roles = [
  { id: 'shopper', label: 'Shopper', description: 'Shop from local boutiques', icon: '🛍️' },
  { id: 'business', label: 'Business', description: 'Sell your products', icon: '🏪' },
  { id: 'driver', label: 'Driver', description: 'Deliver orders', icon: '🚗' },
];

export function SignUpPage() {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [role, setRole] = useState<UserRole>('shopper');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, name, role);
      // Business users go to onboarding, everyone else goes home
      if (role === 'business') {
        navigate('/business/onboarding');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src={FASH_LOGO} alt="Fash" className="h-24 w-24 object-contain mx-auto" />
          </Link>
          <p className="text-body mt-2">Join the fashion revolution</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">Create account</CardTitle>
            <CardDescription>
              {step === 1 ? 'Choose how you want to use Fash' : 'Enter your details'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Step Indicator */}
            <div className="flex items-center justify-center space-x-2 mb-6">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                step >= 1 ? "bg-primary text-white" : "bg-gray-200 text-gray-500"
              )}>
                1
              </div>
              <div className="w-12 h-0.5 bg-gray-200">
                <div className={cn("h-full bg-primary transition-all", step >= 2 ? "w-full" : "w-0")} />
              </div>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                step >= 2 ? "bg-primary text-white" : "bg-gray-200 text-gray-500"
              )}>
                2
              </div>
            </div>

            {error && (
              <div className="bg-error/10 text-error text-sm p-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {step === 1 ? (
              <div className="space-y-4">
                <p className="text-sm text-body text-center mb-4">I want to:</p>
                <div className="space-y-3">
                  {roles.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setRole(r.id as UserRole)}
                      className={cn(
                        "w-full flex items-center p-4 rounded-xl border-2 transition-all text-left",
                        role === r.id
                          ? "border-primary bg-primary/5"
                          : "border-gray-100 hover:border-gray-200"
                      )}
                    >
                      <span className="text-2xl mr-4">{r.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium text-heading">{r.label}</p>
                        <p className="text-sm text-body">{r.description}</p>
                      </div>
                      {role === r.id && (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <Button 
                  type="button" 
                  fullWidth 
                  size="lg"
                  onClick={() => setStep(2)}
                >
                  Continue with email
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-gray-500">or</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  type="button"
                  fullWidth
                  size="lg"
                  loading={googleLoading}
                  onClick={async () => {
                    setGoogleLoading(true);
                    setError(null);
                    try {
                      const user = await signInWithGoogle(role);
                      if (user) {
                        if (role === 'business') {
                          navigate('/business/onboarding');
                        } else {
                          navigate('/');
                        }
                      }
                    } catch (err: any) {
                      setError(err.message || 'Google sign-up failed');
                    } finally {
                      setGoogleLoading(false);
                    }
                  }}
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Jane Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {/* Password requirements */}
                  <div className="space-y-1">
                    {passwordRequirements.map((req, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-xs">
                        <div className={cn(
                          "w-4 h-4 rounded-full flex items-center justify-center",
                          req.met ? "bg-success" : "bg-gray-200"
                        )}>
                          {req.met && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className={req.met ? "text-success" : "text-body"}>{req.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-heading">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      error={confirmPassword.length > 0 && password !== confirmPassword}
                      required
                    />
                  </div>
                </div>

                {/* Terms */}
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-body">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                  </span>
                </label>

                <div className="flex space-x-3">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    fullWidth 
                    loading={loading} 
                    size="lg"
                    disabled={!agreedToTerms}
                  >
                    Create Account
                  </Button>
                </div>
              </form>
            )}

            <p className="text-center text-sm text-body mt-6">
              Already have an account?{' '}
              <Link to="/signin" className="text-primary font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


